import React, { useState, useEffect, useCallback } from "react";
import Container from "../../../layouts/Container";
import FamilyGroupTable from "../components/FamilyGroupTable";
import Spinner from "../../../components/Spinner";
import SearchBar from "../../../components/SearchBar";
import ButtonForm from "../../../components/ButtonForm";
import Modal from "../../../components/Modal";
import Toaster from "../../../components/Toaster";

import { getFamilyGroups, deleteFamilyGroup, createFamilyGroup, updateFamilyGroup } from "../../../services/apiFamilyGroups";
import { getAllOrganizations } from "../../../services/apiOrganizations";
import { useAuth } from "../../../providers/AuthContext";
import { useAppContext } from "../../../providers/AppContext";
import { useNavigate } from "react-router-dom";

function FamilyGroups() {
  const navigate = useNavigate();
  const { auth, logout } = useAuth();
  const { addToast } = useAppContext();

  const [familyGroups, setFamilyGroups] = useState([]);
  const [organizations, setOrganizations] = useState([]);

  const [isLoading, setIsLoading] = useState(true);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState(null);
  const [toastConfig, setToastConfig] = useState(null);

  const [formModalOpen, setFormModalOpen] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const showError = (msg) => {
    setErrorMessage(msg);
    setErrorModalOpen(true);
  };
  const [formData, setFormData] = useState({ nombre: '', nombre_admin: '', cedula_admin: '', organizationId: '', isActive: true });
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const [sortBy, setSortBy] = useState(
    JSON.stringify({ column: "createdAt", isAscending: true })
  );

  const [searchTerm, setSearchTerm] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [elementsPerPage, setElementsPerPage] = useState(10);
  const pageSizes = [2, 5, 10, 20];

  const [searchOrgTerm, setSearchOrgTerm] = useState("");

  const filteredOrganizations = organizations.filter((organization) =>
    organization.name.toLowerCase().includes(searchOrgTerm.toLowerCase())
  );

  useEffect(() => {
    async function fetchOrganizations() {
      if (!auth?.token) return;
      getAllOrganizations(auth?.token)
        .then(organizationsData => {
          setOrganizations(organizationsData);
        })
        .catch(error => {
          if ([403].includes(error.response?.status)) {
            addToast("No autorizado", 2);
            navigate('/logout');
          } else if ([419].includes(error.response?.status)) {
            addToast("Sesión expirada", 1);
            navigate('/logout');
          } else {
            logout();
            navigate('/login');
          }
        });
    }
    fetchOrganizations();
  }, [auth]);

  const refreshElements = useCallback(() => {
    if (!auth?.token) return;
    setIsLoading(true);
    getFamilyGroups('', auth?.token)
      .then(data => {
        const groups = Array.isArray(data) ? data : (data.records || []);
        setFamilyGroups(groups);
      })
      .catch(error => {
        if ([403].includes(error.response?.status)) {
          addToast("No autorizado", 2);
          navigate('/logout');
        } else if ([419].includes(error.response?.status)) {
          addToast("Sesión expirada", 1);
          navigate('/logout');
        } else if ([401].includes(error.response?.status)) {
          logout();
          navigate('/login');
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [auth]);

  useEffect(() => {
    refreshElements();
  }, [refreshElements]);

  const handleEditGroup = (group) => {
    setFormData({
      nombre: group.nombre || '',
      nombre_admin: group.nombre_admin || '',
      cedula_admin: group.cedula_admin || '',
      organizationId: group.organizationId || '',
      isActive: group.isActive !== undefined ? group.isActive : true
    });
    setEditId(group._id || group.id);
    setIsEditing(true);
    setFormModalOpen(true);
  };

  const handleDeleteGroup = (group) => {
    setGroupToDelete(group);
    setDeleteModalOpen(true);
  };

  const confirmDeleteGroup = async () => {
    if (!groupToDelete) return;
    const groupId = groupToDelete._id || groupToDelete.id;
    setDeleteModalOpen(false);

    try {
      await deleteFamilyGroup(groupId, auth.token);
      await refreshElements();
      setToastConfig({ message: "Grupo Familiar Eliminado", type: 0 });
    } catch (error) {
      console.error("Error al eliminar grupo:", error);
      showError(error.message || "Hubo un error al eliminar el grupo.");
    } finally {
      setGroupToDelete(null);
    }
  };

  const handleCreateGroup = () => {
    setFormData({ nombre: '', nombre_admin: '', cedula_admin: '', organizationId: '', isActive: true });
    setSearchOrgTerm('');
    setIsEditing(false);
    setEditId(null);
    setFormModalOpen(true);
  };

  const handleSaveGroup = async () => {
    if (!formData.nombre) {
      showError("El nombre es requerido");
      return;
    }

    if (!formData.nombre_admin) {
      showError("El nombre del administrador es requerido");
      return;
    }

    if (!formData.cedula_admin || formData.cedula_admin.length !== 10) {
      showError("La cédula del administrador debe tener exactamente 10 dígitos");
      return;
    }

    if (!formData.organizationId) {
      showError("La organización es requerida");
      return;
    }

    setIsSaving(true);
    try {
      if (isEditing) {
        await updateFamilyGroup(editId, formData, auth.token);
        setToastConfig({ message: "Grupo Familiar Actualizado", type: 0 });
      } else {
        await createFamilyGroup(formData, auth.token);
        setToastConfig({ message: "Grupo Familiar Creado", type: 0 });
      }
      setFormModalOpen(false);
      refreshElements();
    } catch (error) {
      console.error("Error al guardar grupo:", error);
      showError(error.message || "Hubo un error al guardar el grupo.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleHeaderClick = (column) => {
    const currentSort = JSON.parse(sortBy);
    const newIsAscending =
      currentSort.column === column ? !currentSort.isAscending : true;
    setSortBy(JSON.stringify({ column, isAscending: newIsAscending }));

    // Sort local data since backend might not support it yet
    const sortedData = [...familyGroups].sort((a, b) => {
      let valA = a[column];
      let valB = b[column];

      if (typeof valA === 'string') valA = valA.toLowerCase();
      if (typeof valB === 'string') valB = valB.toLowerCase();

      if (valA < valB) return newIsAscending ? -1 : 1;
      if (valA > valB) return newIsAscending ? 1 : -1;
      return 0;
    });
    setFamilyGroups(sortedData);
    setCurrentPage(1);
  };

  const filteredGroups = familyGroups.filter(group =>
    group.nombre?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalElements = filteredGroups.length;
  const totalPages = Math.ceil(totalElements / elementsPerPage) || 1;
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const startPage = currentPage === totalPages ? Math.max(1, currentPage - 2) : Math.max(1, currentPage - 1);
  const endPage = Math.min(totalPages, startPage + 2);

  const paginatedGroups = filteredGroups.slice(
    (currentPage - 1) * elementsPerPage,
    currentPage * elementsPerPage
  );

  return (
    <Container>
      {toastConfig && (
        <Toaster
          message={toastConfig.message}
          type={toastConfig.type}
          onClose={() => setToastConfig(null)}
        />
      )}
      <div className="w-11/12 mx-auto p-4 my-2">
        <div className="flex items-center justify-between my-2">
          <SearchBar
            type="search"
            text="Filtrar por nombre"
            id="search"
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1); // Reset to first page on search
            }}
            value={searchTerm}
          />
          <ButtonForm
            text="Crear Grupo Familiar"
            enabled={true}
            action={handleCreateGroup}
          />
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <Spinner />
          </div>
        ) : (
          <FamilyGroupTable
            familyGroups={paginatedGroups}
            onEdit={handleEditGroup}
            onDelete={handleDeleteGroup}
            onSort={handleHeaderClick}
            sortBy={sortBy}
            organizationsParent={organizations}
          />
        )}

        <div className="mt-4 flex justify-between items-center">
          <div className="flex justify-between items-center">
            <p className="mr-2">Total de Grupos Familiares:</p>
            <span className={`text-3xl font-bold transition-all duration-500 text-bluesens`}>
              {totalElements}
            </span>
          </div>

          {familyGroups?.length > 0 && (
            <div className="flex flex-col justify-center gap-y-2 items-center">
              <span>Navegación entre Páginas</span>
              <div className="flex gap-x-1 items-center border-2 rounded p-2">
                <button
                  onClick={() => paginate(1)}
                  disabled={currentPage === 1}
                  className={`px-3 py-1 rounded-md border-2 border-bluesens border-solid disabled:opacity-50 disabled:border-none`}
                >
                  <img
                    src={`${process.env.PUBLIC_URL}/svg/skip_previous.svg`}
                    alt="First Page"
                    className="h-5 w-5"
                  />
                </button>

                <button
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`px-3 py-1 rounded-md border-2 border-bluesens border-solid disabled:opacity-50 disabled:border-none`}
                >
                  <img
                    src={`${process.env.PUBLIC_URL}/svg/arrow_back.svg`}
                    alt="Previous Page"
                    className="h-5 w-5"
                  />
                </button>

                {Array.from({ length: endPage - startPage + 1 }, (_, index) =>
                  index + 1 <= endPage - startPage ||
                  totalPages <= 3 ||
                  totalPages - 1 <= currentPage ? (
                    <button
                      key={startPage + index}
                      onClick={() => paginate(startPage + index)}
                      className={`px-3 py-1 rounded-md ${
                        currentPage === startPage + index
                          ? "bg-bluesens text-white"
                          : "bg-gray-200"
                      }`}
                    >
                      {startPage + index}
                    </button>
                  ) : (
                    <React.Fragment key={totalPages}>
                      <span>...</span>
                      <button
                        onClick={() => paginate(totalPages)}
                        className={`px-3 py-1 rounded-md ${
                          currentPage === startPage + index
                            ? "bg-blue-500 text-white"
                            : "bg-gray-200"
                        }`}
                      >
                        {totalPages}
                      </button>
                    </React.Fragment>
                  )
                )}

                <button
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`px-3 py-1 rounded-md border-2 border-bluesens border-solid disabled:opacity-50 disabled:border-none`}
                >
                  <img
                    src={`${process.env.PUBLIC_URL}/svg/arrow_forward.svg`}
                    alt="Next Page"
                    className="h-5 w-5"
                  />
                </button>

                <button
                  onClick={() => paginate(totalPages)}
                  disabled={currentPage === totalPages}
                  className={`px-3 py-1 rounded-md border-2 border-bluesens border-solid disabled:opacity-50 disabled:border-none`}
                >
                  <img
                    src={`${process.env.PUBLIC_URL}/svg/skip_next.svg`}
                    alt="Last Page"
                    className="h-5 w-5"
                  />
                </button>
              </div>
            </div>
          )}

          {familyGroups?.length > 0 && (
            <div>
              <label htmlFor="pageSize" className="mr-2">
                Elementos por Página:
              </label>
              <select
                id="pageSize"
                value={elementsPerPage}
                onChange={(e) => {
                  setElementsPerPage(parseInt(e.target.value, 10));
                  setCurrentPage(1); // Reset to page 1 when page size changes
                }}
                className="border rounded-md py-1 px-2"
              >
                {pageSizes.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      <Modal
        isOpen={deleteModalOpen}
        title="Confirmar Eliminación"
        showCloseButton={false}
      >
        <div className="p-4 text-left">
          <p className="text-gray-700 mb-6">
            ¿Estás seguro de que deseas eliminar este grupo familiar? Esta acción no se puede deshacer.
          </p>
          <div className="flex justify-end gap-4">
            <button
              onClick={confirmDeleteGroup}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition font-bold"
            >
              Eliminar
            </button>
            <button
              onClick={() => setDeleteModalOpen(false)}
              className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400 transition font-bold"
            >
              Cancelar
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={formModalOpen}
        onClose={() => setFormModalOpen(false)}
        title={isEditing ? "Editar Grupo Familiar" : "Crear Grupo Familiar"}
      >
        <div className="p-6 text-left">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Nombre del Grupo</label>
            <input
              type="text"
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#10174c]"
              value={formData.nombre}
              onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Nombre del Administrador</label>
            <input
              type="text"
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#10174c]"
              value={formData.nombre_admin}
              onChange={(e) => setFormData({ ...formData, nombre_admin: e.target.value })}
            />
          </div>
          <div className="mb-8">
            <label className="block text-sm font-medium text-gray-700 mb-1">Cédula del Administrador</label>
            <input
              type="text"
              className={`w-full border ${formData.cedula_admin.length > 0 && formData.cedula_admin.length !== 10 ? 'border-red-500' : 'border-gray-300'} rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#10174c]`}
              value={formData.cedula_admin}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "");
                if (value.length <= 10) {
                  setFormData({ ...formData, cedula_admin: value });
                }
              }}
            />
            {formData.cedula_admin.length > 0 && formData.cedula_admin.length !== 10 && (
              <p className="text-red-500 text-xs italic mt-1">Debe tener exactamente 10 dígitos</p>
            )}
          </div>
          <div className="mb-8">
            <label className="block text-sm font-medium text-gray-700 mb-1">Organización</label>
            <div className="mb-4 flex gap-x-4 items-center">
              <SearchBar
                type="search"
                id="search-bar-organization-familyGroupForm"
                text={`Buscar Organización`}
                onChange={(e) => setSearchOrgTerm(e.target.value)}
                value={searchOrgTerm}
              />
              <span className="text-l font-bold uppercase">{`${filteredOrganizations.length} Disponible(s)`}</span>
            </div>
            <div className="grid grid-cols-2 gap-4 max-h-[12rem] overflow-y-auto">
              {filteredOrganizations.map((organization) => (
                <button
                  key={organization._id || organization.id}
                  type="button"
                  onClick={() => setFormData({ ...formData, organizationId: organization._id || organization.id })}
                  className={`px-4 py-2 rounded-md ${
                    formData.organizationId === (organization._id || organization.id)
                      ? "bg-blue-500 text-white"
                      : "bg-gray-200 text-gray-700"
                  }`}
                >
                  {organization.name}
                </button>
              ))}
            </div>
          </div>
          <div className="mb-8 flex items-center">
            <label className="block text-sm font-medium text-gray-700 mr-4">Grupo Activo</label>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#10174c]"></div>
            </label>
          </div>
          <div className="flex justify-end gap-4 mt-6 pt-4 border-t border-gray-200">
            <button
              onClick={() => setFormModalOpen(false)}
              className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400 transition"
              disabled={isSaving}
            >
              Cancelar
            </button>
            <button
              onClick={handleSaveGroup}
              className="px-4 py-2 bg-[#10174c] text-white rounded hover:bg-blue-900 transition flex items-center justify-center min-w-[100px]"
              disabled={isSaving}
            >
              {isSaving ? "Guardando..." : "Guardar"}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={errorModalOpen}
        onClose={() => setErrorModalOpen(false)}
        title=""
        size="w-full max-w-sm"
        colorTitle="bg-red-500"
        customType={2}
      >
        <div className="p-4 text-left">
          <p className="text-gray-700 font-medium text-center">{errorMessage}</p>
        </div>
      </Modal>
    </Container>
  );
}

export default FamilyGroups;
